import { useI18n } from 'next-localization';

import { Badges } from '@/components/badges';
import { ModuleLink } from '@/components/module-link/module-link';
import { ProceduresAndCondition } from '@/features/profile/profile.types';
import { useBasePath } from '@/hooks/use-base-path';
import { useCustomRouting } from '@/hooks/use-custom-routing';
import { cn, getPhysicianProfileUrl } from '@/lib/utils';

import styles from '../../listing.module.css';

const MAX_PROCEDURES_AND_CONDITIONS_TO_SHOW = 3;

type ProceduresAndConditionsProps = {
  proceduresAndConditions: ProceduresAndCondition[];
  urlTitle: string;
  physicianName: string;
  acceptsNewPatients: boolean;
  telehealthProvider: boolean;
};

export const ProceduresAndConditionsSection = ({
  proceduresAndConditions,
  urlTitle,
  physicianName,
  acceptsNewPatients,
  telehealthProvider,
}: ProceduresAndConditionsProps): JSX.Element => {
  const { t } = useI18n();
  const { withBasePath } = useBasePath();
  const { routerPush } = useCustomRouting();

  const uniqueProcedures = Array.from(
    new Set(proceduresAndConditions?.map((cond) => cond?.keyword))
  );

  return (
    <div className={cn('col-lg-5', styles.proceduresWrapper)}>
      {uniqueProcedures?.length > 0 && (
        <div className="d-flex flex-column">
          <h5 className={styles.addressCardTitle}>{t('OAS.Listing.ProceduresAndConditions')}</h5>

          {uniqueProcedures?.length > 0 &&
            uniqueProcedures?.slice(0, MAX_PROCEDURES_AND_CONDITIONS_TO_SHOW)?.map((el, index) => (
              <span key={`${el}-${+index}`} className={styles.proceduresText}>
                {el}
              </span>
            ))}

          {uniqueProcedures?.length > MAX_PROCEDURES_AND_CONDITIONS_TO_SHOW && (
            <ModuleLink
              onClick={(e) => {
                e.preventDefault();
                routerPush({
                  pathname: withBasePath(getPhysicianProfileUrl(urlTitle)),
                  query: {
                    'scroll-to-procedures-and-conditions': 'true',
                  },
                });
              }}
              style={{ width: 'fit-content' }}
              href="#"
              className="mt-1"
              prefetch={false}
            >
              {t('OAS.Listing.SeeAll')} ({uniqueProcedures.length})
              <span className="sr-only">
                {`${t('OAS.Listing.Accessibility.ProceduresAndConditionsFor')} ${physicianName}`}
              </span>
            </ModuleLink>
          )}
        </div>
      )}

      <div className="col col-sm-8 mt-2 d-md-none">
        <Badges
          acceptsNewPatients={acceptsNewPatients}
          telehealthProvider={telehealthProvider}
          className="mb-2"
        />
      </div>
    </div>
  );
};
